/**
 * @ipc/admin-ui · entities/session · model/hooks — TanStack Query хуки сессии.
 * useSessionQuery: GET /admin/auth/me (авторитет текущего пользователя для RBAC/гейтов).
 * useLoginMutation / useLogoutMutation: вход/выход + инвалидация сессии.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys, getAccessToken, ApiError } from '@/shared/api';
import { login, logout, fetchSession } from '../api/session-api';
import type { SessionUser } from './types';
import type { TelegramLoginDto } from './schema';

/**
 * Текущая сессия. enabled только при наличии accessToken — без токена не дёргаем /me
 * (гейт сам уведёт на /login). 401 внутри request → single-flight refresh; при
 * окончательном провале refresh api-client чистит токены и редиректит на /login.
 */
export function useSessionQuery(): UseQueryResult<SessionUser | null, ApiError> {
  return useQuery<SessionUser | null, ApiError>({
    queryKey: queryKeys.session(),
    queryFn: () => fetchSession(),
    enabled: getAccessToken() !== null,
    staleTime: 60_000,
    retry: false,
  });
}

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TelegramLoginDto) => login(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.session() });
    },
  });
}

export function useLogoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      qc.clear();
    },
  });
}
